import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';

// Axios instance for media endpoints (without /user/ prefix)
const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/`
);

export interface CVAnalysisResponse {
  overallScore: number;
  scoreBucket: string;
  assessment?: string;
  executiveSummary?: string;
  scorecard?: {
    atsOptimisation?: {score: number; explanation: string};
    technicalAcumen?: {score: number; explanation: string};
    impactQuantification?: {score: number; explanation: string};
    ownershipAndScale?: {score: number; explanation: string};
    roleAlignment?: {score: number; explanation: string};
    craftSignals?: {score: number; explanation: string};
    resumeHygiene?: {score: number; explanation: string};
  };
  strengths: Array<{title: string; description: string}>;
  improvements: Array<{title: string; description: string}>;
  experience?: {min: number; max: number};
  topSkills?: string[];
  currentDesignation?: string;
  functionalArea?: number;
  careerPath?: {
    roadmap: string;
    focusAreas: Array<{title: string; description: string}>;
    interviewReadinessNote?: string;
  };
  error?: {
    message: string;
    code: number;
    errorType?: string;
  };
}

export interface CVAnalyzerLimitError {
  error: {
    message: string;
    code: number;
    errorType?: string;
  };
}

const analyzeCV = async (
  formData: FormData
): Promise<CVAnalysisResponse | CVAnalyzerLimitError> => {
  try {
    const response = await axios.post<
      | {data: CVAnalysisResponse; message: string; statusCode: number}
      | CVAnalysisResponse
    >('media/cv-analyser', formData);

    const analysisData =
      'data' in response.data && response.data.data
        ? response.data.data
        : response.data;

    if (analysisData && typeof (analysisData as any).error === 'string') {
      return {
        error: {
          message:
            (analysisData as any).message ||
            'Failed to analyze CV. Please try again.',
          code: 400,
          errorType: (analysisData as any).error,
        },
      } as CVAnalyzerLimitError;
    }

    return analysisData as CVAnalysisResponse;
  } catch (error: any) {
    // Handle different error scenarios
    if (error.response) {
      const {status, data} = error.response;

      // Rate limit error (429/403)
      if (status === 429 || status === 403) {
        const rateLimitError: CVAnalyzerLimitError = {
          error: {
            message:
              data?.error?.message ||
              "You've reached the CV Analyzer limit for this device. Please try again later.",
            code: 429,
          },
        };
        return rateLimitError;
      }

      // System error (500+)
      if (status >= 500) {
        const systemError: CVAnalyzerLimitError = {
          error: {
            message:
              data?.error?.message ||
              'Failed to analyze your CV. Please try again later.',
            code: 500,
          },
        };
        return systemError;
      }

      // Other API errors (400, etc.)
      const apiError: CVAnalyzerLimitError = {
        error: {
          message:
            data?.error?.message || 'An error occurred during CV analysis.',
          code: status,
        },
      };
      return apiError;
    }

    // Network error
    const networkError: CVAnalyzerLimitError = {
      error: {
        message: 'Network error. Please check your connection and try again.',
        code: 0,
      },
    };
    return networkError;
  }
};

interface FetchCVAnalyserJobsParams {
  functionalArea?: number;
  skills?: string;
  experience?: number;
  experienceUpperLimit?: number;
}

const fetchCVAnalyserJobs = async (
  params: FetchCVAnalyserJobsParams
): Promise<any[]> => {
  try {
    const query = new URLSearchParams();
    if (params.functionalArea != null)
      query.set('functionalArea', String(params.functionalArea));
    if (params.skills) query.set('skills', params.skills);
    if (params.experience != null)
      query.set('experience', String(params.experience));
    if (params.experienceUpperLimit != null)
      query.set('experienceUpperLimit', String(params.experienceUpperLimit));

    const response = await axios.get<{data: {data: any[]}}>(
      `job/cv-analyser-jobs?${query.toString()}`
    );
    return response.data?.data?.data ?? [];
  } catch {
    return [];
  }
};

const cvAnalyzerService = {
  analyzeCV,
  fetchCVAnalyserJobs,
};

export default cvAnalyzerService;
